import {useCallback, useMemo, useState} from "react";
import {User} from "../api/accessControl/User.ts";
import {BackendApi} from "../api/api.ts";
import {useNotifications} from "../lib/NotificationContext.tsx";
import {TransactionToBook} from "../components/specific/BookTransactionModal/BookTransactionModal.tsx";

interface CategoryChangeGlAccount {
    id: string;
}

interface UseTransactionCategoryChangeOptions {
    currentUser: User;
    defaultGlAccount: CategoryChangeGlAccount;
    onCategoryChanged: () => Promise<void>;
}

export function useTransactionCategoryChange({
    currentUser,
    defaultGlAccount,
    onCategoryChanged,
}: UseTransactionCategoryChangeOptions) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const {dispatch} = useNotifications();
    const [transactionToBook, setTransactionToBook] = useState<TransactionToBook | null>(null);
    const [isCategoryChangeLoading, setIsCategoryChangeLoading] = useState(false);

    const openCategoryChange = useCallback((transaction: TransactionToBook) => {
        setTransactionToBook(transaction);
    }, []);

    const closeCategoryChange = useCallback(() => {
        setTransactionToBook(null);
    }, []);

    const handleCategoryChangeSubmit = useCallback(async (
        transaction: TransactionToBook,
        glAccountToBook: CategoryChangeGlAccount,
        feedback: string | null,
        subledgerId: string | null
    ) => {
        setIsCategoryChangeLoading(true);
        try {
            const isDefaultGlAccount = glAccountToBook.id === defaultGlAccount.id;
            await api.CategorizationEngine__recategorizeTransaction({
                general_ledger_account_id: isDefaultGlAccount ? null : glAccountToBook.id,
                feedback: feedback ?? '',
                transaction_id: transaction.id,
                subledger_id: subledgerId,
            });
            setTransactionToBook(null);
            await onCategoryChanged();
            dispatch({
                title: 'Transactie hercategoriseerd',
                description: 'De transactie is succesvol hercategoriseerd.',
                type: 'success',
            });
        } catch (error) {
            console.error("Error recategorizing transaction:", error);
            setTransactionToBook(null);
            dispatch({
                title: 'Fout bij hercategoriseren',
                description: 'Er is een fout opgetreden. Probeer het later opnieuw.',
                type: 'error',
            });
        } finally {
            setIsCategoryChangeLoading(false);
        }
    }, [api, defaultGlAccount, dispatch, onCategoryChanged]);

    return {
        transactionToBook,
        isCategoryChangeLoading,
        openCategoryChange,
        closeCategoryChange,
        handleCategoryChangeSubmit,
    };
}
